import { alert } from "@components/common/Alert";
import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import { usePreferences } from "@hooks";
import { useOnboardingMilestones } from "@hooks/use-onboarding-milestones";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useEffectEvent, useState } from "react";
import { useNavigate } from "react-router";

import CelebrationBanner from "./CelebrationBanner";
import MilestoneRow from "./MilestoneRow";
import ottoRocket from "../../assets/imgs/onboarding/otto-in-rocket.png";

export default function OnboardingMilestoneChecklist() {
  const { releaseWebOnboardingMilestones } = useFlags();
  const {
    milestones,
    progress,
    prompt,
    justCompleted,
    allComplete,
    isOnboardingComplete,
  } = useOnboardingMilestones();
  const {
    setFirstTimeTourDisabled,
    setReviewNoteTourSeen,
    setExportNoteTourSeen,
  } = usePreferences();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationDismissed, setCelebrationDismissed] = useState(
    () =>
      localStorage.getItem("onboarding-celebration-dismissed") ===
      "true",
  );

  const onJustCompleted = useEffectEvent(() => {
    setShowCelebration(true);
  });

  useEffect(() => {
    if (justCompleted) {
      onJustCompleted();
    }
  }, [justCompleted]);

  if (!releaseWebOnboardingMilestones) {
    return null;
  }

  // Legacy user: onboarding was marked complete (old flow) but milestones
  // weren't tracked. Don't intrude on their workflow with a checklist.
  if (isOnboardingComplete && !allComplete) {
    return null;
  }

  // Already completed and user has dismissed the celebration — don't render
  if (
    isOnboardingComplete &&
    !showCelebration &&
    celebrationDismissed
  ) {
    return null;
  }

  // Celebration banner: show when just completed or on return visits until dismissed
  if (
    showCelebration ||
    (isOnboardingComplete && !celebrationDismissed)
  ) {
    return (
      <CelebrationBanner
        dataTestId="onboarding-celebration"
        onDiscoverMore={() => {
          localStorage.setItem(
            "onboarding-celebration-dismissed",
            "true",
          );
          setCelebrationDismissed(true);
          navigate("/account?tab=onboarding#discover-more");
        }}
      />
    );
  }

  const currentMilestone = milestones.find((m) => m.current);

  const handleShowMeHow = () => {
    if (!currentMilestone?.showMeHow) return;

    if (currentMilestone.showMeHow === "ftue") {
      setFirstTimeTourDisabled(false);
    } else if (currentMilestone.showMeHow === "review-tour") {
      if (
        !document.querySelector(
          '[data-testid="note-ready-for-review"]',
        )
      ) {
        alert(
          "warning",
          "You don't have any notes ready for review yet. Record a note first, then try this step when you have a note ready for review in your Inbox!",
        );
        return;
      }
      setReviewNoteTourSeen(false);
    } else if (currentMilestone.showMeHow === "export-tour") {
      setExportNoteTourSeen(false);
      navigate("/inbox?tab=forExport");
    } else {
      navigate(currentMilestone.showMeHow);
    }
  };

  return (
    <DSCardWrapper
      variant="info"
      dataTestId="onboarding-milestone-checklist"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-brand">
            {prompt.bold}
          </h3>
          {prompt.rest && (
            <p className="mt-xs text-sm text-secondary">
              {prompt.rest}
            </p>
          )}
        </div>

        <div className="flex items-center gap-sm shrink-0">
          {currentMilestone?.showMeHow && (
            <div className="animate-attention-pulse rounded-full">
              <DSButton
                variant="primary"
                size="md"
                dataTestId="milestone-show-me-how"
                onClick={handleShowMeHow}
              >
                Show Me How
              </DSButton>
            </div>
          )}
          <DSButton
            variant="secondary"
            size="sm"
            icon={isExpanded ? faChevronUp : faChevronDown}
            dataTestId="toggle-milestones"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Collapse" : "Show Progress"}
          </DSButton>
        </div>
      </div>

      {/* Rocket progress bar */}
      <div className="mt-md relative">
        <div className="h-2.5 rounded-full overflow-hidden bg-tertiary">
          <div
            className="h-full bg-brand rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <img
          src={ottoRocket}
          alt="Otto in Rocket"
          className="absolute -top-7 transition-all duration-500"
          style={{
            left: `calc(${progress}% - 20px + ${
              progress === 0 ? 20 : progress === 100 ? -20 : 0
            }px)`,
            width: 40,
            height: "auto",
            transform: "rotate(90deg)",
            pointerEvents: "none",
            userSelect: "none",
          }}
          draggable={false}
        />
        <span className="mt-xs block text-right text-xs font-semibold text-brand">
          {progress}%
        </span>
      </div>

      {/* Expanded milestone list */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isExpanded
            ? "max-h-[400px] opacity-100 mt-md"
            : "max-h-0 opacity-0"
        }`}
      >
        <div className="border-t border-secondary pt-sm space-y-0.5">
          {milestones.map((m) => (
            <MilestoneRow
              key={m.key}
              icon={m.icon}
              label={m.label}
              completed={m.completed}
              current={m.current}
            />
          ))}
        </div>
      </div>
    </DSCardWrapper>
  );
}
